import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  ClipboardList,
  Receipt,
  Users,
  Inbox,
  FileText,
  Shield,
  LogOut,
  PlusCircle,
  Plug,
} from "lucide-react";

function SidebarLink({ to, icon: Icon, label, end, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 px-4 py-2.5 text-sm ${
          isActive
            ? "text-white bg-white/5 border-l-2 border-white"
            : "text-slate-400 hover:text-white border-l-2 border-transparent"
        }`
      }
    >
      <Icon className="h-4 w-4" strokeWidth={2} />
      <span className="tracking-tight">{label}</span>
    </NavLink>
  );
}

export default function DashboardLayout({ variant = "client" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = variant === "admin";

  const clientNav = [
    { to: "/portal", icon: LayoutDashboard, label: "Overview", end: true, testId: "nav-portal-overview" },
    { to: "/portal/work-orders", icon: ClipboardList, label: "Work orders", testId: "nav-portal-work-orders" },
    { to: "/portal/invoices", icon: Receipt, label: "Invoices", testId: "nav-portal-invoices" },
  ];

  const adminNav = [
    { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true, testId: "nav-admin-overview" },
    { to: "/admin/work-orders", icon: ClipboardList, label: "Work orders", testId: "nav-admin-work-orders" },
    { to: "/admin/invoices", icon: Receipt, label: "Invoices", testId: "nav-admin-invoices" },
    { to: "/admin/clients", icon: Users, label: "Clients", testId: "nav-admin-clients" },
    { to: "/admin/contacts", icon: Inbox, label: "Contact inbox", testId: "nav-admin-contacts" },
    { to: "/admin/quotes", icon: FileText, label: "Quote requests", testId: "nav-admin-quotes" },
    { to: "/admin/integrations", icon: Plug, label: "Integrations", testId: "nav-admin-integrations" },
  ];

  const nav = isAdmin ? adminNav : clientNav;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex">
      <aside
        data-testid="dashboard-sidebar"
        className="w-64 shrink-0 bg-[#0B192C] text-slate-300 min-h-screen flex flex-col sticky top-0"
      >
        <Link to="/" className="p-5 flex items-center gap-3 border-b border-white/10">
          <div className="h-8 w-8 bg-white/10 flex items-center justify-center">
            <Shield className="h-4 w-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-white text-sm tracking-tight">MEAD PROPERTY</div>
            <div className="text-[9px] uppercase tracking-[0.28em] text-slate-500">
              {isAdmin ? "Admin console" : "Client portal"}
            </div>
          </div>
        </Link>

        <nav className="flex-1 py-4">
          {nav.map((item) => (
            <SidebarLink key={item.to} {...item} />
          ))}
        </nav>

        {!isAdmin && (
          <div className="px-4 pb-4">
            <Link
              to="/portal/work-orders/new"
              data-testid="sidebar-new-wo"
              className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-medium rounded-sm"
            >
              <PlusCircle className="h-4 w-4" /> New work order
            </Link>
          </div>
        )}

        <div className="border-t border-white/10 p-4">
          <div className="text-xs text-slate-400 truncate">{user?.email}</div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mt-0.5">
            {user?.role}
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="mt-3 w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:text-white border border-white/10 hover:border-white/30 rounded-sm"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-6">
          <div className="text-xs uppercase tracking-[0.28em] text-slate-500">
            {isAdmin ? "Admin console" : "Client portal"}
          </div>
          <div className="text-sm text-slate-600">
            Welcome, <span className="text-slate-900 font-medium">{user?.name || user?.email}</span>
          </div>
        </div>
        <div className="p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
